// Shared constants + formatting helpers for the Rate Card Builder screen.

// All bases the API understands, with their per-unit labels. Used for badges,
// unit suffixes, and legacy-card tolerance. Only `sqft` and `unit` are offered
// to users going forward (see PRICED_BY_OPTIONS).
export const LABOR_BASIS_OPTIONS = [
  { value: "sqft", label: "Square foot", unit: "hr/sqft" },
  { value: "lineal_ft", label: "Lineal foot", unit: "hr/lin-ft" },
  { value: "face", label: "Largest face", unit: "hr/face-sqft" },
  { value: "unit", label: "Per piece", unit: "hr/piece" },
];

// The go-forward "Priced by" choice: exactly two options.
export const PRICED_BY_OPTIONS = [
  { value: "sqft", label: "Square foot", unit: "hr/sqft" },
  { value: "unit", label: "Per piece", unit: "hr/piece" },
];

// Muted "(legacy)" chip labels shown only when editing an existing card whose
// stored basis is one of the retired options.
export const LEGACY_BASIS_LABELS = {
  lineal_ft: "Lineal foot (legacy)",
  face: "Largest face (legacy)",
};

export const GEOMETRY_OPTIONS = [
  { value: "flat_2d", label: "Flat panel (W×H)" },
  { value: "column_wrap_4face", label: "4-side wrap" },
  { value: "surface_area_3d", label: "3D surface" },
];

/**
 * Infer the geometry method for a NEW card from its category text.
 * Category containing "column" (case-insensitive) → 4-side wrap; else flat.
 */
export function inferGeometryMethod(category) {
  const text = (category || "").toLowerCase();
  return text.includes("column") ? "column_wrap_4face" : "flat_2d";
}

/**
 * Plain-text description of where square footage comes from, for the fine-print
 * line under the "Priced by" chips. Only meaningful for the sqft basis.
 */
export function sqftSourceLabel(geometryMethod) {
  switch (geometryMethod) {
    case "column_wrap_4face":
      return "Square footage from: 4-side wrap";
    case "surface_area_3d":
      return "Square footage from: 3D surface area";
    case "flat_2d":
    default:
      return "Square footage from: flat panel (W×H)";
  }
}

export const STATUS_OPTIONS = [
  { value: "devised", label: "Devised" },
  { value: "confirmed", label: "Confirmed" },
  { value: "needs_adjustment", label: "Needs adjustment" },
  { value: "candidate", label: "Candidate" },
];

// One-line, plain-English meaning of each status, shown live under the select.
// These are bookkeeping labels only — they do not change pricing behavior.
export const STATUS_DESCRIPTIONS = {
  devised:
    "Set from Andy's method or a meeting; not yet checked against real jobs.",
  confirmed: "Validated against real job history. Trust it.",
  needs_adjustment: "Validation flagged a gap; usable but review the rates.",
  candidate:
    "AI-suggested from job history; needs Andy's confirmation before going live.",
};

export const SIZE_BAND_DIM_OPTIONS = [
  { value: "width", label: "Width" },
  { value: "height", label: "Height" },
  { value: "length", label: "Projection" },
  { value: "max", label: "Largest dimension" },
  { value: "cross_max", label: "Column width (larger cross-section)" },
];

// The "Band by:" chips shown in the band-set editor — shop language, one per
// card family. `value` is the size_band_dim the engine understands.
export const BAND_BY_CHIPS = [
  { value: "width", label: "Width" },
  { value: "height", label: "Height" },
  { value: "length", label: "Projection" },
  { value: "max", label: "Longest side" },
  { value: "cross_max", label: "Column width" },
];

// One-line fine print explaining each "Band by" choice.
export const BAND_BY_HINTS = {
  width: "Bands read the part's width.",
  height: "Bands read the part's height.",
  length: "Bands read the part's projection (depth).",
  max: "Bands read the longest of width, height, and projection.",
  cross_max:
    "Measures the larger cross-section (2nd-largest overall) — right for columns.",
};

/** Short human label for a size_band_dim value. */
export function bandDimLabel(dim) {
  const chip = BAND_BY_CHIPS.find((c) => c.value === dim);
  return chip ? chip.label : dim || "";
}

/**
 * The dimension (in inches) a band range is measured against, given a scenario's
 * width/height/length. `max` = longest side; `cross_max` = the 2nd-largest of
 * the three (the larger cross-section, used by columns).
 */
export function relevantDimForBand(dim, dims) {
  const w = Number(dims?.width) || 0;
  const h = Number(dims?.height) || 0;
  const l = Number(dims?.length) || 0;
  switch (dim) {
    case "width":
      return w;
    case "height":
      return h;
    case "length":
      return l;
    case "max":
      return Math.max(w, h, l);
    case "cross_max": {
      const sorted = [w, h, l].sort((a, b) => b - a);
      return sorted[1] || 0;
    }
    default:
      return 0;
  }
}

const bandNum = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);

/**
 * Given band rows [{size_band_min_in, size_band_max_in, ...}] and a measured
 * value, return the row whose [min, max) range contains it (max null = "and up",
 * min null/blank = 0). Returns null if nothing matches.
 */
export function matchBand(bands, value) {
  if (!Array.isArray(bands)) return null;
  return (
    bands.find((b) => {
      const min = bandNum(b.size_band_min_in);
      const max = bandNum(b.size_band_max_in);
      const lo = min === null ? 0 : min;
      const hasMax = max !== null;
      return value >= lo && (!hasMax || value < max);
    }) || null
  );
}

const BASIS_UNIT_MAP = LABOR_BASIS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.unit;
  return acc;
}, {});

/** Basis-aware unit suffix for labor rate inputs (e.g. "hr/sqft"). */
export function unitForBasis(basis) {
  return BASIS_UNIT_MAP[basis] || "hr/sqft";
}

/** Format a number as $X,XXX.XX. Non-numeric -> "—". */
export function formatMoney(value) {
  const num = Number(value);
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(num)
  ) {
    return "—";
  }
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Format a number with up to `digits` decimals, trimming trailing zeros. */
export function formatNum(value, digits = 2) {
  const num = Number(value);
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(num)
  ) {
    return "0";
  }
  return Number(num.toFixed(digits)).toString();
}

/** Compact human label for a size band, e.g. "6–12"" or "" when unbanded. */
export function bandLabel(card) {
  if (!card || !card.size_band_dim) return "";
  const min = card.size_band_min_in;
  const max = card.size_band_max_in;
  const hasMin = min !== null && min !== undefined && min !== "";
  const hasMax = max !== null && max !== undefined && max !== "";
  if (!hasMin && !hasMax) return "";
  const lo = hasMin ? formatNum(min, 2) : "0";
  const hi = hasMax ? formatNum(max, 2) : "∞";
  return `${lo}–${hi}"`;
}
